import moment from 'moment'

export function pct(amount, total) {
  if (total === null || total === undefined || Number(total) === 0) {
    return null
  }
  if (amount === null || amount === undefined || !Number.isFinite(Number(amount))) {
    return null
  }
  return (Number(amount) / Number(total)) * 100
}

export function sumField(rows, getter) {
  return rows.reduce((sum, row) => sum + (Number(getter(row)) || 0), 0)
}

export function appendVisibleRows(row, rows, isExpanded) {
  rows.push(row)
  if (!row.hasChildren || !isExpanded(row.key)) {
    return
  }
  const children = row.children || []
  children.forEach((child) => appendVisibleRows(child, rows, isExpanded))
}

// Mecanica comun de los subpaneles de dashboard con tabla-arbol de carga perezosa.
// El componente debe implementar fetchNodeChildren(parentId) devolviendo los nodos hijos (null → raiz).
export default {
  props: {
    projectId: {
      type: [Number, String],
      default: null,
    },
    projectName: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      default: '€',
    },
  },

  data() {
    return {
      progressMode: 'inspected', // 'inspected' | 'actual'
      isTableLoading: false,
      rootGroups: [],
      childrenByParentId: {},
      loadedParentIds: {},
      loadingParentIds: {},
      loadingExpandKey: null,
      expandedGroups: {},
    }
  },

  computed: {
    currencySymbol() {
      return this.currency || '€'
    },

    cutoffMoment() {
      return moment().subtract(1, 'day').endOf('day')
    },

    cutoffLabel() {
      const m = this.cutoffMoment
      const dayMonth = m.clone().locale(this.$i18n.locale || 'es').format('D-MMM')
      const weekday = m.clone().locale(this.$i18n.locale || 'es').format('dddd')
      return `${dayMonth} | ${weekday}`
    },

    periodDates() {
      const prevMonthEnd = moment().startOf('month').subtract(1, 'day').format('YYYY-MM-DD')
      const monthStart = moment().startOf('month').format('YYYY-MM-DD')
      const monthEnd = moment().endOf('month').format('YYYY-MM-DD')
      const cutoff = this.cutoffMoment.format('YYYY-MM-DD')
      const hasCutoffInCurrentMonth = !moment(cutoff).isBefore(monthStart, 'day')

      return {
        prevMonthEnd,
        monthStart,
        monthEnd,
        cutoff,
        hasCutoffInCurrentMonth,
        // APIs require end >= start. On day 1 cutoff is still last month, so clamp to current month start. (asi corrige que el 1 de septiembre ponia como 'end' 31 agosto, asi mantiene end a 1 septiembre)
        periodEnd: hasCutoffInCurrentMonth ? cutoff : monthStart,
      }
    },
  },

  watch: {
    projectId: {
      immediate: true,
      handler(projectId) {
        if (projectId) {
          this.loadRootGroups()
        } else {
          this.resetGroupData()
        }
      },
    },
  },

  methods: {
    resetGroupData() {
      this.rootGroups = []
      this.childrenByParentId = {}
      this.loadedParentIds = {}
      this.loadingParentIds = {}
      this.loadingExpandKey = null
      this.expandedGroups = {}
    },

    async loadRootGroups() {
      if (!this.projectId) {
        return
      }

      this.isTableLoading = true
      this.resetGroupData()

      try {
        this.rootGroups = await this.fetchNodeChildren(null)
        this.loadedParentIds = { root: true }
      } catch (error) {
        this.rootGroups = []
        this.$notify.error((error && error.error_msg) || this.$t('error_retrieving_data'))
      } finally {
        this.isTableLoading = false
      }
    },

    async loadChildren(parentId) {
      const cacheKey = String(parentId)
      if (this.loadedParentIds[cacheKey]) {
        return
      }

      this.loadingParentIds = {
        ...this.loadingParentIds,
        [cacheKey]: true,
      }

      try {
        const children = await this.fetchNodeChildren(parentId)
        this.childrenByParentId = {
          ...this.childrenByParentId,
          [parentId]: children,
        }
        this.loadedParentIds = {
          ...this.loadedParentIds,
          [cacheKey]: true,
        }
      } catch (error) {
        this.$notify.error((error && error.error_msg) || this.$t('error_retrieving_data'))
      } finally {
        const nextLoading = { ...this.loadingParentIds }
        delete nextLoading[cacheKey]
        this.loadingParentIds = nextLoading
        this.loadingExpandKey = null
      }
    },

    isRowLoadingChildren(row) {
      return this.loadingExpandKey === row.key || Boolean(this.loadingParentIds[String(row.id)])
    },

    async ensureChildrenLoaded(row) {
      if (!row || !row.hasChildren || this.loadedParentIds[String(row.id)]) {
        return
      }
      this.loadingExpandKey = row.key
      await this.loadChildren(row.id)
    },

    async toggleGroupExpand(row) {
      if (!row || !row.hasChildren) {
        return
      }

      const willExpand = !this.isGroupExpanded(row.key)
      if (willExpand) {
        await this.ensureChildrenLoaded(row)
        this.expandedGroups = {
          ...this.expandedGroups,
          [row.key]: true,
        }
        return
      }

      this.expandedGroups = {
        ...this.expandedGroups,
        [row.key]: false,
      }
    },

    isGroupExpanded(key) {
      return Boolean(this.expandedGroups[key])
    },

    rowIndentStyle(row) {
      if (!row.depth) {
        return null
      }
      return { paddingLeft: `${0.5 + row.depth * 1.25}rem` }
    },

    rowAccentStyle(row) {
      if (row.isProject || row.depth === 0) {
        return null
      }
      const accentColors = ['#ADC2EA', '#7D99D4', '#5577BB', '#385DA5']
      return {
        backgroundColor: row.depth % 2 === 1 ? '#f1f5f9' : '#e8eef5',
        boxShadow: `inset ${Math.min(row.depth, 4) * 3}px 0 0 ${accentColors[Math.min(row.depth - 1, accentColors.length - 1)]}`,
      }
    },

    rowLabelClass(row) {
      if (row.isProject || row.depth === 0) {
        return 'font-semibold text-grey-darkest'
      }
      if (row.depth === 1) {
        return 'text-sm text-grey-darker font-normal'
      }
      return 'text-xs text-grey-dark font-normal'
    },

    summaryBarHeight(percent, maxPx) {
      if (percent === null || percent === undefined || !Number.isFinite(Number(percent))) {
        return '8px'
      }
      const clamped = Math.max(0, Math.min(100, Number(percent)))
      return `${Math.max(8, (clamped / 100) * maxPx)}px`
    },

    hasNumericValue(value) {
      return value !== null && value !== undefined && Number.isFinite(Number(value))
    },

    formatAmount(value) {
      if (!this.hasNumericValue(value)) {
        return '—'
      }
      return Number(value).toLocaleString('es-ES', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      })
    },

    formatSignedAmount(value) {
      if (!this.hasNumericValue(value)) {
        return '—'
      }
      const num = Number(value)
      const formatted = Math.abs(num).toLocaleString('es-ES', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      })
      return num < 0 ? `-${formatted}` : formatted
    },

    formatPercent(value) {
      if (!this.hasNumericValue(value)) {
        return '—'
      }
      return `${Number(value).toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}%`
    },

    formatSignedPercent(value) {
      if (!this.hasNumericValue(value)) {
        return '—'
      }
      const num = Number(value)
      const formatted = Math.abs(num).toLocaleString('es-ES', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return num < 0 ? `-${formatted}%` : `${formatted}%`
    },

    deviationClass(value) {
      if (!this.hasNumericValue(value) || Number(value) >= 0) {
        return ''
      }
      return 'monetary-table__deviation-negative'
    },
  },
}
